import { X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, type PointerEvent, type ReactNode } from "react";
import { useVisualEffects } from "../../hooks/VisualEffectsContext";

const FOCUSABLE = "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex='-1'])";

export function handleBottomSheetKeyDown(
  event: Pick<KeyboardEvent, "key" | "shiftKey" | "preventDefault">,
  focusable: HTMLElement[],
  activeElement: Element | null,
  onClose: () => void,
) {
  if (event.key === "Escape") { event.preventDefault(); onClose(); return; }
  if (event.key !== "Tab" || focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable.at(-1)!;
  if (event.shiftKey && (activeElement === first || !focusable.includes(activeElement as HTMLElement))) {
    event.preventDefault(); last.focus();
  } else if (!event.shiftKey && (activeElement === last || !focusable.includes(activeElement as HTMLElement))) {
    event.preventDefault(); first.focus();
  }
}

export function shouldDismissSheet(distance: number, elapsed: number) {
  return distance >= 90 || (distance >= 24 && distance / Math.max(elapsed, 1) > .45);
}

export function BottomSheet({ open, title, onClose, children, className = "" }: {
  open: boolean; title: string; onClose: () => void; children: ReactNode; className?: string;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const { reduced } = useVisualEffects();
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;
  const drag = useRef<{ id: number; y: number; start: number; distance: number } | null>(null);
  const animation = useRef<Animation | null>(null);
  const closing = useRef(false);

  const dismiss = useCallback((instant = false) => {
    if (closing.current) return;
    closing.current = true;
    const sheet = sheetRef.current;
    animation.current?.cancel();
    if (instant || reducedRef.current || !sheet?.animate) {
      closeRef.current();
    } else {
      animation.current = sheet.animate([
        { transform: sheet.style.transform || "translateY(0)", opacity: 1 },
        { transform: "translateY(32px)", opacity: 0 },
      ], { duration: 160, easing: "cubic-bezier(.23, 1, .32, 1)", fill: "forwards" });
      animation.current.onfinish = () => closeRef.current();
    }
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    // Dialog native làm nền inert; giữ focus và âm thanh ngoài sheet độc lập.
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closing.current = false;
    dialog.showModal();
    if (!reducedRef.current && !trigger?.matches(":focus-visible")) {
      animation.current = sheetRef.current?.animate([
        { transform: "translateY(32px)", opacity: 0 },
        { transform: "translateY(0)", opacity: 1 },
      ], { duration: 240, easing: "cubic-bezier(.32, .72, 0, 1)" }) ?? null;
    }
    return () => {
      animation.current?.cancel();
      drag.current = null;
      if (sheetRef.current) sheetRef.current.style.transform = "";
      dialog.close();
      document.body.style.overflow = overflow;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [open]);

  function endDrag(event: PointerEvent<HTMLDivElement>, cancelled = false) {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (!cancelled && shouldDismissSheet(current.distance, performance.now() - current.start)) {
      dismiss();
    } else if (sheetRef.current) {
      const sheet = sheetRef.current;
      if (!reduced) sheet.animate([{ transform: sheet.style.transform }, { transform: "translateY(0)" }], { duration: 180, easing: "cubic-bezier(.23, 1, .32, 1)" });
      sheet.style.transform = "";
    }
  }

  return (
    <dialog ref={dialogRef} className={`gothic-sheet-dialog ${className}`.trim()} aria-labelledby={titleId}
      onCancel={(event) => { event.preventDefault(); dismiss(true); }}
      onClick={(event) => { if (event.target === event.currentTarget) dismiss(); }}
      onKeyDown={(event) => handleBottomSheetKeyDown(event,
        Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter((item) => item.getClientRects().length > 0),
        document.activeElement, () => dismiss(true))}>
      {open && <div ref={sheetRef} className="bottom-sheet">
        <div className="bottom-sheet-drag-zone" aria-hidden="true"
          onPointerDown={(event) => {
            if (!event.isPrimary || event.button !== 0 || drag.current || closing.current) return;
            animation.current?.cancel();
            drag.current = { id: event.pointerId, y: event.clientY, start: performance.now(), distance: 0 };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const current = drag.current;
            if (!current || current.id !== event.pointerId) return;
            current.distance = event.clientY - current.y;
            // Chỉ kéo từ handle, không giành thao tác cuộn hoặc chỉnh âm lượng.
            if (sheetRef.current && !reduced) sheetRef.current.style.transform = `translateY(${current.distance > 0 ? current.distance : current.distance * .12}px)`;
          }}
          onPointerUp={(event) => endDrag(event)} onPointerCancel={(event) => endDrag(event, true)}>
          <span className="bottom-sheet-handle" />
        </div>
        <header className="bottom-sheet-header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-button" onClick={(event) => dismiss(event.detail === 0)} aria-label={`Đóng ${title}`}><X /></button>
        </header>
        <div className="bottom-sheet-body">{children}</div>
      </div>}
    </dialog>
  );
}
